import React, { useState, useRef, useEffect } from 'react'
import { useUsers } from '@/hooks/queries/useUsers'
import { useHOD } from '@/hooks/queries/useHOD'
import { useCreateUser, useUpdateUser, useDeleteUser, useMassDeleteUsers } from '@/hooks/mutations/users'
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom'; // Ensure this is imported
import { userService } from '@/services/userService'
import HODLink from './HODLink'
import Pagination from '../Pagination'

const headers = [
  // { name: '', styles: 'align-top', inputType: 'text', searchIcon: true },

  { name: '', styles: 'align-top text-left', inputType: 'text', searchIcon: true },
  {
    name: 'Name',
    styles: 'min-w-[180px] text-left',
    inputType: 'text',
    searchIcon: false,
  },
  {
    name: 'Email',
    styles: 'min-w-[250px] text-left',
    inputType: 'text',
    searchIcon: false,
  },
  { name: 'Advisors', styles: 'min-w-[200px] text-left' },
  { name: '', styles: 'min-w-[80px] text-left' },
  // {
  //   name: 'Name/ID',
  //   styles: 'min-w-[160px]',
  //   inputType: 'text',
  //   searchIcon: false,
  // },
  // {
  //   name: 'Position',
  //   styles: 'min-w-[130px]',
  //   inputType: 'text',
  //   arrowIcon: true,
  // },
  // {
  //   name: 'BDay',
  //   styles: 'min-w-[170px]',
  //   inputType: 'date',
  //   calendarIcon: true,
  // },
  // {
  //   name: 'Email/Phone',
  //   styles: 'min-w-[220px]',
  //   inputType: 'text',
  //   searchIcon: true,
  // },
  // {
  //   name: 'Address',
  //   styles: 'min-w-[220px]',
  //   inputType: 'text',
  //   arrowIcon: true,
  // },
  // {
  //   name: 'Status',
  //   styles: 'min-w-[120px]',
  //   inputType: 'text',
  //   arrowIcon: true,
  // },
  // { name: '', styles: 'min-w-[100px]' },
]

export const Head_of_department = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [searchParams, setSearchParams] = useState({})
  const [selectedUsers, setSelectedUsers] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const searchInputRef = useRef(null)
  
  // Use the new React Query hook
  const { 
    data: hodData, 
    isLoading, 
    isError, 
    error: queryError 
  } = useHOD(currentPage, searchParams);
  
  // Extract HODs and pagination from the query result
  const advisors = hodData?.data || [];
  const paginationData = hodData?.pagination || {
    current_page: 1,
    total: 0,
    per_page: 10,
    last_page: 1
  };

  // handling the input changes with debouncing for empty input
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // We'll debounce the empty string check to avoid immediate API calls on backspace
    if (value === '') {
      // Set a small timeout to debounce clearing
      setTimeout(() => {
        setDebouncedSearchTerm('');
      }, 300);
    } else {
      setDebouncedSearchTerm(value);
    }
  };
  
  // Effect to auto-clear search when input becomes empty
  useEffect(() => {
    if (debouncedSearchTerm === '' && Object.keys(searchParams).length > 0) {
      clearSearch();
    }
  }, [debouncedSearchTerm]);

  // Add a function to handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    
    // Create a new searchParams object based on the searchTerm
    const newParams = {};
    
    // Check if searchTerm contains @ which likely means it's an email
    if (searchTerm.includes('@')) {
      newParams.email = searchTerm;
    } else if (searchTerm.trim()) {
      newParams.name = searchTerm;
    }
    
    // Update search params and reset to first page
    setSearchParams(newParams);
    setCurrentPage(1);
    
    // Maintain focus on the search input
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };
  
  // Add a function to clear the search
  const clearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSearchParams({});
    setCurrentPage(1);
    
    // Maintain focus on the search input after clearing
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Create loading indicator component to reuse
  const LoadingIndicator = () => (
    <div className="flex justify-center py-4">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#F16745]"></div>
    </div>
  );

  const handlePageChange = (pageNumber) => {
    console.log('Page change clicked:', pageNumber);
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  // Add the delete user mutation
  const deleteUser = useDeleteUser();
  
  const handleDelete = async (id) => {
    try {
      await deleteUser.mutateAsync(id);
      toast.success('User deleted successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
    }
  };

  // We'll use the API filtering now instead of client-side filtering
  const filteredAdvisors = advisors;
  
  // Show error message if there is an error
  if (isError) return <div className='pt-10'>Error loading users: {queryError?.message || 'An error occurred'}</div>

  return (
    <div className="px-7 sm:px-9 lg:px-10 py-12 w-full mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#F16745] dark:text-[#F16745] mb-2">Head of Department</h1>
          <p className="text-base text-body-color dark:text-dark-6">Manage and monitor department heads</p>
        </div>
      </div>

      <div className="bg-white rounded-lg dark:bg-dark-2 shadow-card">
        {/* Search Section - always visible even during loading */}
        <div className="flex items-center justify-between flex-wrap sm:flex-nowrap gap-4 border-b border-stroke dark:border-dark-3 pt-[8px] pb-[18px]">
          <div className="w-full sm:w-auto sm:flex-1 max-w-full sm:max-w-[420px]">
            <form onSubmit={handleSearch}>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                  ref={searchInputRef}
                type="text"
                placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                style={{ boxShadow: 'none' }}
                className="h-12 w-full pl-10 pr-4 rounded-md border border-gray-300 bg-white text-gray-700 placeholder:text-gray-400 hover:border-gray-400 focus:border-[#F16745] focus:outline-none focus:ring-0 transition-colors"
              />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-[46px] top-0 h-full px-2 flex items-center justify-center text-gray-400 hover:text-gray-600"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                <button 
                  type="submit"
                  className="absolute top-0 right-0 flex h-full w-[46px] items-center justify-center bg-[#F16745] text-white rounded-r-md"
                >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.2687 15.6657L12.6281 11.897C14.5406 9.28135 14.3437 5.54072 11.9531 3.17822C10.6875 1.9126 8.99995 1.20947 7.19995 1.20947C5.39995 1.20947 3.71245 1.9126 2.44683 3.17822C-0.168799 5.79385 -0.168799 10.0688 2.44683 12.6845C3.71245 13.9501 5.39995 14.6532 7.19995 14.6532C8.91558 14.6532 10.5187 14.0063 11.7843 12.8532L16.4812 16.6501C16.5937 16.7345 16.7343 16.7907 16.875 16.7907C17.0718 16.7907 17.2406 16.7063 17.3531 16.5657C17.5781 16.2845 17.55 15.8907 17.2687 15.6657ZM7.19995 13.3876C5.73745 13.3876 4.38745 12.8251 3.34683 11.7845C1.20933 9.64697 1.20933 6.1876 3.34683 4.07822C4.38745 3.0376 5.73745 2.4751 7.19995 2.4751C8.66245 2.4751 10.0125 3.0376 11.0531 4.07822C13.1906 6.21572 13.1906 9.6751 11.0531 11.7845C10.0406 12.8251 8.66245 13.3876 7.19995 13.3876Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
            </form>
          </div>
          <button 
            onClick={() => navigate('/users/create', { 
              state: { 
                role: 'Head of Department',
                from: '/head_of_department' 
              } 
            })}
            className="group relative inline-flex items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-[#F16745] to-[#F16745]/80 p-0.5 text-sm font-medium text-white hover:text-white focus:outline-none focus:ring-4 focus:ring-[#F16745]/50 whitespace-nowrap"
          >
            <span className="relative flex items-center gap-2 rounded-md bg-[#F16745] px-5 py-2.5 transition-all duration-75 ease-in group-hover:bg-opacity-0">
              <span className="whitespace-nowrap text-base">Add HOD</span>
            </span>
          </button>
        </div>
        
        {/* Desktop Table View */}
        <div className="hidden lg:block">
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <TableHead 
                selectedUsers={selectedUsers} 
                setSelectedUsers={setSelectedUsers} 
                users={advisors}
              />
              {isLoading ? (
                <tbody>
                  <tr>
                    <td colSpan={headers.length} className="py-8 text-center">
                      <LoadingIndicator />
                    </td>
                  </tr>
                </tbody>
              ) : filteredAdvisors.length > 0 ? (
                <TableBody 
                  advisors={filteredAdvisors} 
                  selectedUsers={selectedUsers} 
                  setSelectedUsers={setSelectedUsers}
                  onDelete={handleDelete}
                />
              ) : (
                <tbody>
                  <tr>
                    <td colSpan={headers.length} className="text-center py-8 text-gray-500">
                      No HOD found 
                    </td>
                  </tr>
                </tbody>
              )}
            </table>
          </div>
        </div>

        {/* Mobile/Tablet View */}
        <div className="lg:hidden space-y-4 p-4">
          {isLoading ? (
            <LoadingIndicator />
          ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {filteredAdvisors.length > 0 ? (
              filteredAdvisors.map((advisor) => (
                <div key={advisor.id} className="bg-white p-4 rounded-lg shadow border border-stroke">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-[#F16745] font-medium truncate">{advisor.name}</h3>
                        <p className="text-sm mt-3 text-gray-600 break-all">{advisor.email}</p>
                        </div>
                      <div className="flex-shrink-0">
                        <Dropdown 
                          userId={advisor.id}
                          onDelete={() => handleDelete(advisor.id)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-600">Advisors:</p>
                        <div className="flex-1">
                          <HODLink hod={advisor} />
                        </div>
                      </div>
                    </div>

                    <div className="pt-2">
                      <Checkbox 
                        index={advisor.id}
                        isChecked={selectedUsers.includes(advisor.id)}
                        onChange={(checked) => {
                          if (checked) {
                            setSelectedUsers([...selectedUsers, advisor.id])
                          } else {
                            setSelectedUsers(selectedUsers.filter(id => id !== advisor.id))
                          }
                        }}
                        label="Select HOD"
                      />
                    </div>
                  </div>
              </div>
              ))
            ) : (
              <div className="col-span-2 text-center text-gray-500 py-4">
                No HOD found matching your search criteria
              </div>
            )}
          </div>
          )}
        </div>

        {/* Pagination - don't hide during fetching, only during initial loading */}
        {filteredAdvisors.length > 0 && !isLoading && (
          <div className="w-full">
            <Pagination
              currentPage={paginationData.current_page}
              totalPages={paginationData.last_page}
              onPageChange={handlePageChange}
              itemsPerPage={paginationData.per_page}
              totalItems={paginationData.total}
            />
          </div>
        )}
      </div>
    </div>
  )
}

const TableHead = ({ selectedUsers, setSelectedUsers, users }) => {
  return (
    <thead>
      <tr className="border-b border-stroke dark:border-dark-3">
        {headers.map((header, index) => (
          <th
            className={`px-4 pt-9 pb-6 first:pl-8 align-top ${header.styles}`}
            key={index}
          >
            {index === 0 && users?.length > 0 && (
              <Checkbox 
                index="selectAll"
                isChecked={users.length > 0 && selectedUsers.length === users.length}
                onChange={(checked) => {
                  if (checked) {
                    setSelectedUsers(users.map(user => user.id))
                  } else {
                    setSelectedUsers([])
                  }
                }}
              />
            )}
            {index !== 0 && (
              <div>
                <div className="mb-[10px] flex items-center">
                  <p className="mr-1 text-base font-medium text-body-color dark:text-dark-6">
                    {header.name}
                  </p>
                </div>
              </div>
            )}
          </th>
        ))}
      </tr>
    </thead>
  )
}

const TableBody = ({ advisors, selectedUsers, setSelectedUsers, onDelete }) => {
  const updateUser = useUpdateUser();

  const handleUpdate = async (id, userData) => {
    try {
      await updateUser.mutateAsync({ id, data: userData })
      toast.success('User updated successfully')
    } catch (error) {
      toast.error(error.message || 'Failed to update user')
    }
  }

  return (
    <tbody>
      {advisors.map((advisor, index) => (
        <tr
          className='border-b border-stroke dark:border-dark-3 hover:bg-[#F16745]/5'
          key={advisor.id}
        >
          <td className='py-5 pl-6'>
            <Checkbox 
              index={advisor.id}
              isChecked={selectedUsers.includes(advisor.id)}
              onChange={(checked) => {
                if (checked) {
                  setSelectedUsers([...selectedUsers, advisor.id])
                } else {
                  setSelectedUsers(selectedUsers.filter(id => id !== advisor.id))
                }
              }}
            />
          </td>
          <td className='py-5 px-4'>
            <p className='text-base font-medium text-[#F16745]'>{advisor.name}</p>
          </td>
          <td className='py-5 px-4'>
            <p className='text-base text-body-color dark:text-dark-6'>{advisor.email}</p>
          </td>
          <td className='py-5 px-4'>
            <HODLink hod={advisor} />
          </td>
          <td className='py-5 px-4'>
            <p className='text-base text-body-color dark:text-dark-6'>{advisor.phone}</p>
          </td>
          <td className='py-5 px-4'>
            <p className='text-base text-body-color dark:text-dark-6'>{advisor.address}</p>
          </td>
          <td className='py-5 px-4'>
            <p className='text-base text-body-color dark:text-dark-6'>{advisor.status}</p>
          </td>
          <td className='py-5 pl-4 pr-8'>
            <Dropdown 
              userId={advisor.id}
              onDelete={() => onDelete(advisor.id)}
            />
          </td>
        </tr>
      ))}
    </tbody>
  )
}

const Dropdown = ({ userId, onDelete }) => {
  const navigate = useNavigate()
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const trigger = useRef(null)
  const dropdown = useRef(null)

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return
      setDropdownOpen(false)
    }
    document.addEventListener('click', clickHandler)
    return () => document.removeEventListener('click', clickHandler)
  })

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return
      setDropdownOpen(false)
    }
    document.addEventListener('keydown', keyHandler)
    return () => document.removeEventListener('keydown', keyHandler)
  })

  return (
    <div className="relative inline-block">
      <button
        ref={trigger}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="text-body-color dark:text-dark-6 p-1"
      >
        <svg
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 12C10 10.8954 10.8954 10 12 10C13.1046 10 14 10.8954 14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 5C10 3.89543 10.8954 3 12 3C13.1046 3 14 3.89543 14 5C14 6.10457 13.1046 7 12 7C10.8954 7 10 6.10457 10 5Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 19C10 17.8954 10.8954 17 12 17C13.1046 17 14 17.8954 14 19C14 20.1046 13.1046 21 12 21C10.8954 21 10 20.1046 10 19Z"
            fill="currentColor"
          />
        </svg>
      </button>

      {dropdownOpen && (
        <div
          ref={dropdown}
          className="fixed z-50 w-[120px] rounded-md bg-white p-2 shadow-lg dark:bg-dark border-stroke dark:border-dark-3 border"
          style={{
            top: trigger.current ? trigger.current.getBoundingClientRect().bottom + 5 : 0,
            left: trigger.current ? trigger.current.getBoundingClientRect().left - 100 : 0
          }}
        >
          <button 
            onClick={() => {
              navigate(`/users/edit/${userId}`);
              setDropdownOpen(false);
            }}
            className="w-full px-5 py-2 text-sm text-left rounded text-body-color hover:bg-[#F16745] hover:text-white dark:text-dark-6 dark:hover:bg-dark-2"
          >
            Edit
          </button>
          {/* <button 
            onClick={() => {
              onDelete();
              setDropdownOpen(false);
            }}
            className="w-full px-5 py-2 text-sm text-left rounded text-body-color hover:bg-[#F16745] hover:text-white dark:text-dark-6 dark:hover:bg-dark-2"
          >
            Delete
          </button> */}
        </div>
      )}
    </div>
  )
}

const Checkbox = ({ index, isChecked, onChange }) => {
  return (
    <div className='relative mt-1'>
      <input
        type='checkbox'
        name='tableCheckbox'
        id={index}
        checked={isChecked}
        onChange={(e) => onChange(e.target.checked)}
        className='tableCheckbox sr-only'
      />
      <label
        htmlFor={index}
        className={`flex h-[18px] w-[18px] cursor-pointer items-center justify-center rounded border ${
          isChecked ? '!bg-[#F16745] border-[#F16745]' : 'border-stroke dark:border-dark-4'
        }`}
      >
        <span className={`icon ${isChecked ? '' : 'opacity-0'} text-white`}>
          <svg
            width='14'
            height='14'
            viewBox='0 0 14 14'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M12.0791 3.08687C12.307 3.31468 12.307 3.68402 12.0791 3.91183L5.66248 10.3285C5.43467 10.5563 5.06533 10.5563 4.83752 10.3285L1.92085 7.41183C1.69305 7.18402 1.69305 6.81468 1.92085 6.58687C2.14866 6.35906 2.51801 6.35906 2.74581 6.58687L5.25 9.09106L11.2542 3.08687C11.482 2.85906 11.8513 2.85906 12.0791 3.08687Z'
              fill='currentColor'
            />
          </svg>
        </span>
      </label>
    </div>
  )
}

export default Head_of_department;